/**
 * productPhotoRules: what a product photo has to be, said once.
 * ───────────────────────────────────────────────────────────────────────────
 * Lusuku asks for a product cover that is JPG or PNG, at least 1000 x 1000 px
 * and no more than 10 MB, and shows it inside a square on white (the 2000 x 2000 `c_pad,b_white`
 * master, controllers/mediaController.js). The import workbooks we hand to
 * vendors never said any of that (reported 4 Oct 2026), so a vendor could
 * shoot a whole catalogue at the wrong size and find out one product at a
 * time.
 *
 * The restaurant, healthcare and catalogue upload forms refuse a smaller
 * photo; the brand create and edit forms state the rule but do not check
 * pixels (4 Oct 2026).
 *
 * The workbooks, the import dialogs and every product upload box read these
 * words from here. The menu workbook is a static styled file
 * (public/templates/Lusuku_Menu_Import_Template.xlsx) and carries the same
 * sentence by hand: change both together.
 *
 * Kept free of imports so the Node build script for the static template can
 * load it as well as the app.
 */

export const PRODUCT_PHOTO_RULE = Object.freeze({
  formats: Object.freeze(["JPG", "PNG"]),
  minSidePx: 1000,
  maxMB: 10,
});

const { formats, minSidePx, maxMB } = PRODUCT_PHOTO_RULE;
const SIZE = `${minSidePx} x ${minSidePx} px`;

/** One line, for a hint under an upload box or a column note. */
export const PRODUCT_PHOTO_RULE_TEXT = `${formats.join(" or ")}, square, at least ${SIZE}, up to ${maxMB} MB`;

/**
 * The line under a product upload box: format, size and weight, so a vendor
 * sees the expected dimensions next to the size limit (Izi, 4 Oct 2026):
 * "JPG or PNG · square, at least 1000 x 1000 px · up to 10 MB". Every product
 * upload box reads it. A box that takes more formats than the rule (the brand
 * edit form also takes WebP) names its own.
 */
export const productPhotoHint = (formatsText = formats.join(" or ")) =>
  `${formatsText} · square, at least ${SIZE} · up to ${maxMB} MB`;

/** The fuller version, as rows for a workbook's READ ME sheet. */
export const PRODUCT_PHOTO_README_ROWS = [
  ["PHOTOS"],
  ["·", `Format: ${formats.join(" or ")}. Up to ${maxMB} MB per photo.`],
  ["·", `Size: square, at least ${SIZE}. 2000 x 2000 px is ideal. Smaller photos are refused by the upload forms and look soft on Lusuku.`],
  ["·", "Framing: the product centred on a plain white background, filling most of the square, with no text, price tags or watermarks."],
  ["·", "A photo that is not square is shown whole, with white added at the sides. Nothing is cropped off."],
  ["·", "Extra Images follow the same rules, up to 5 per product."],
];

/** The stand-in cover Lusuku ships, for rows still waiting on photography. */
export const STAND_IN_PHOTO_FILE = "Lusuku_Product_Photo_Placeholder.png";
